<template>
	<div
		:id="uniqueId"
		class="d-flex flex-column flex-md-row amelipro-messaging-layout"
	>
		<nav
			:id="`${uniqueId}-menu`"
			aria-label="Menu messagerie"
			class="ap-blue darken-1 d-block d-sm-flex d-md-block align-sm-center messaging-menu"
			role="navigation"
			:style="menuStyles"
		>
			<div class="messaging-menu__new-message">
				<AmeliproBtn
					bordered
					class="font-weight-semibold px-5 messaging-menu__new-message-btn"
					:color="newMessageDisable ? 'ap-grey lighten-2' : 'ap-blue darken-1'"
					:disabled="newMessageDisable"
					:hover-color="newMessageDisable ? 'ap-grey lighten-2' : 'ap-blue darken-2'"
					:icon-color="newMessageDisable ? 'ap-grey' : 'ap-white'"
					icon-left
					icon-name="pencilNoCircle"
					:text-color="newMessageDisable ? 'ap-grey' : 'ap-white'"
					@click="emitNewMessageEvent"
				>
					Nouveau message
				</AmeliproBtn>
			</div>

			<div
				v-if="breakpointHelper.isDesktop()"
				class="w-100 messaging-menu--desktop"
			>
				<ul class="list-style-none">
					<li
						v-for="(item, index) in items"
						:key="index"
					>
						<AmeliproMessagingMenuBtn
							:active="item.active"
							:href="item.href"
							:icon="item.icon"
							:label="item.label"
							:to="item.to"
							:unique-id="`${uniqueId}-menu-btn-${index}`"
							:unread-number="item.unreadNumber"
						/>
					</li>
				</ul>
			</div>

			<div
				v-else
				class="w-100 messaging-menu__dropdown-menu"
			>
				<AmeliproDropdownMenu
					:items="menuItems"
					label="Menu de la messagerie"
					:unique-id="`${uniqueId}-dropdown`"
				/>
			</div>
		</nav>

		<div
			:id="`${uniqueId}-content`"
			class="w-100 amelipro-messaging-layout__content"
			:style="mainContentStyles"
		>
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
/* eslint-disable arrow-body-style */
	import { PropType, computed } from 'vue';
	import AmeliproBtn from '../AmeliproBtn';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { DropdownItem } from '../AmeliproDropdownMenu/types';
	import { IndexedObject } from '../../types';
	import { MessagingMenuTypes } from './types';
	import { convertToHex } from '../../functions/convertToHex';

	const props = defineProps({
		items: {
			type: Array as PropType<MessagingMenuTypes[]>,
			default: () => [],
		},
		mainContentBg: {
			type: String,
			default: 'ap-grey lighten-5',
		},
		menuWidth: {
			type: String,
			default: '255px',
		},
		newMessageDisable: {
			type: Boolean,
			default: false,
		},
		uniqueId: {
			type: String,
			required: true,
		},
	});

	const breakpointHelper = new BreakpointHelper();
	const menuItems = computed<DropdownItem[]>(() => {
		return props.items.map((item) => (
			{
				active: item.active,
				href: item.href,
				label: item.label,
				to: item.to,
			}
		));
	});

	const menuStyles = computed<IndexedObject>(() => {
		const styles: IndexedObject = {};
		if (breakpointHelper.isDesktop()) {
			styles.minWidth = (props.menuWidth);
		} else {
			styles.minWidth = '100%';
		}

		return styles;
	});

	const mainContentStyles = computed<IndexedObject>(() => ({ backgroundColor: `${convertToHex(props.mainContentBg)}` }));
	const emit = defineEmits(['click-new-message']);
	const emitNewMessageEvent = (): void => {
		emit('click-new-message');
	};

</script>

<style lang="scss" scoped>
.messaging-menu {
	@media #{$media-only-xs} {
		padding: 0 10px 24px 10px;
	}

	@media #{$media-only-sm} {
		padding: 10px 16px;
	}
}

.messaging-menu__new-message {
	@media #{$media-up-md} {
		display: flex;
		justify-content: center;
		width: 100%;
		margin: 38px auto;
	}

	@media #{$media-only-xs} {
		width: 100%;
		margin: 24px 0 16px 0;
	}

	@media #{$media-only-sm} {
		margin-right: 20px;
	}
}

.messaging-menu__new-message-btn {
	@media #{$media-only-xs} {
		width: 100%;
	}

	&:disabled {
		opacity: 1 !important;
	}
}

.messaging-menu--desktop {
	@media #{$media-up-md} {
		margin-bottom: 30px;
	}
}
</style>
